import { useEffect } from 'react';

/**
 * Unsaved changes + a click on an in-app link (sidebar, header logo) → ask
 * first. BrowserRouter has no useBlocker, so the click is caught in the
 * capture phase on `document`, before react-router's own Link handler, and
 * handed to `onAttempt(href)`. Reload and close are covered by
 * useBeforeUnload.
 *
 * The browser's Back too, through `onHistory(proceed)`. BrowserRouter
 * moves on its own popstate listener, and its `idx` in history.state can't be
 * trusted (a plain replaceState elsewhere, e.g. Dashboard V2's, wipes it for
 * every later entry). So while there are unsaved changes the page adds one
 * extra history entry for itself (same URL). Back then lands on the page's
 * real entry: this listener runs first (CAPTURE phase on window), stops the
 * router hearing it, puts the extra entry back and asks. `proceed` steps back
 * past both. Forward has nothing to guard: the extra entry is always on top.
 * When the changes are saved or dropped, the extra entry is taken away again.
 *
 * When the page is the tab's first entry (a bookmark, a pasted URL)
 * there is nothing to go back to, and go(-2) would do nothing. `onNoHistory`
 * leaves instead (the page goes home).
 *
 * The extra entry is marked with this page load's token. One left by
 * another load (a reload with unsaved changes) is not ours: with nothing
 * unsaved the guard steps off it, and with unsaved changes it takes it over
 * rather than stacking a second one on top.
 */
const GUARD_KEY = 'opsLeaveGuard';

type GuardState = Record<string, unknown> | null;

const LOAD_ID = `${Date.now()}-${Math.random().toString(36).slice(2)}`;

const guardMark = (): unknown =>
  (window.history.state as GuardState)?.[GUARD_KEY];

/** True while the current history entry is the guard's extra entry. */
export const onLeaveGuardEntry = (): boolean => guardMark() === LOAD_ID;

const onStaleGuardEntry = (): boolean =>
  guardMark() !== undefined && guardMark() !== LOAD_ID;

const withoutMark = (state: GuardState): GuardState => {
  if (!state) return state;
  const { [GUARD_KEY]: _mark, ...rest } = state;
  return rest;
};

const keyOf = (state: unknown): unknown => (state as GuardState)?.key;

// history.back() is async, so one removal can be in flight while the form
// flips back to unsaved (ScheduleForm reports clean when it remounts on a
// new day). A guard that arms meanwhile waits for the removal, then puts its
// entry back, or it would be left with none and Back would slip through.
let dropping = false;
let rearm: (() => void) | null = null;

/** Step back off the extra entry without the router seeing the move. */
const dropGuardEntry = () => {
  dropping = true;
  const swallow = (ev: PopStateEvent) => {
    ev.stopImmediatePropagation();
    dropping = false;
    const again = rearm;
    rearm = null;
    again?.();
  };
  window.addEventListener('popstate', swallow, { capture: true, once: true });
  window.history.back();
};

export const useLeaveGuard = (
  active: boolean,
  onAttempt: (href: string) => void,
  onHistory?: (proceed: () => void) => void,
  onNoHistory?: () => void,
) => {
  useEffect(() => {
    if (!onHistory) return undefined;
    if (!active) {
      if (!dropping && onStaleGuardEntry()) dropGuardEntry();
      return undefined;
    }
    const base = withoutMark(window.history.state as GuardState);
    const guard = { ...(base ?? {}), [GUARD_KEY]: LOAD_ID };
    const arm = () => window.history.pushState(guard, '', window.location.href);
    if (dropping) rearm = arm;
    else if (onStaleGuardEntry())
      window.history.replaceState(guard, '', window.location.href);
    else if (!onLeaveGuardEntry()) arm();
    let leaving = false;

    const onPop = (ev: PopStateEvent) => {
      if (leaving) return; // Discard chosen: the router takes this one
      // Back jumped further than this page's own entry (the long-press
      // history menu): the distance is unknown, so let the router move.
      if (
        keyOf(ev.state) !== keyOf(base) ||
        (ev.state as GuardState)?.[GUARD_KEY]
      ) {
        leaving = true;
        return;
      }
      ev.stopImmediatePropagation();
      arm();
      onHistory(() => {
        leaving = true;
        // Just re-armed, so the extra entry is the last one and the page's
        // own entry is at length - 2. At 0 there is nothing before it.
        if (window.history.length > 2 || !onNoHistory) window.history.go(-2);
        else onNoHistory();
      });
    };

    window.addEventListener('popstate', onPop, true);
    return () => {
      window.removeEventListener('popstate', onPop, true);
      if (rearm === arm) {
        rearm = null; // never armed: nothing to take away
        return;
      }
      if (!leaving && onLeaveGuardEntry()) dropGuardEntry();
    };
  }, [active, onHistory, onNoHistory]);

  useEffect(() => {
    if (!active) return undefined;

    const onClick = (ev: MouseEvent) => {
      if (
        ev.defaultPrevented ||
        ev.button !== 0 ||
        ev.metaKey ||
        ev.ctrlKey ||
        ev.shiftKey ||
        ev.altKey
      ) {
        return;
      }
      const target = ev.target as Element | null;
      const anchor = target?.closest?.('a[href]') as HTMLAnchorElement | null;
      if (
        !anchor ||
        anchor.target === '_blank' ||
        anchor.hasAttribute('download')
      ) {
        return;
      }

      const url = new URL(anchor.href, window.location.href);
      if (url.origin !== window.location.origin) return;
      const href = `${url.pathname}${url.search}${url.hash}`;
      const here = `${window.location.pathname}${window.location.search}${window.location.hash}`;
      if (href === here) {
        // Already here. Following it would only replace the guard's
        // entry (react-router replaces on a same-path link).
        ev.preventDefault();
        return;
      }

      ev.preventDefault();
      ev.stopPropagation();
      onAttempt(href);
    };

    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, [active, onAttempt]);
};
